import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { userService } from '../../services/userService';
import { academicService } from '../../services/academicService';
import { BarChart3, Download, Filter, Calendar, TrendingUp, Users, Award } from 'lucide-react';

const ReportsPage = () => {
  const [reports, setReports] = React.useState([]);
  const [metrics, setMetrics] = React.useState({
    totalUsers: 0,
    activeStudents: 0,
    teachers: 0,
    avgAttendance: '0%'
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [generating, setGenerating] = React.useState({});

  const fetchMetrics = async () => {
    try {
      const [studentsRes, classesRes] = await Promise.all([
        userService.getStudents({ limit: 1, role: 'student' }).catch(() => ({ data: { data: { students: [], pagination: { total: 0 } } } })),
        academicService.getClasses({ limit: 1 }).catch(() => ({ data: { data: { classes: [], pagination: { total: 0 } } } }))
      ]);

      const totalStudents = studentsRes.data?.data?.pagination?.total || studentsRes.data?.data?.students?.length || 0;
      const activeStudents = (studentsRes.data?.data?.students || []).filter(s => s.isActive !== false).length;
      
      setMetrics({
        totalUsers: totalStudents,
        activeStudents,
        teachers: 0, // Would need separate API call
        avgAttendance: '0%' // Would need attendance API
      });
    } catch (e) {
      console.error('Error fetching metrics:', e);
    }
  };

  const handleGenerateReport = async (reportType) => {
    try {
      setGenerating(prev => ({ ...prev, [reportType]: true }));
      // For now, reports are generated on-demand when viewing/downloading
      // This would trigger the actual report generation
      alert(`${reportType} report generation would be triggered here`);
    } catch (e) {
      alert(`Failed to generate ${reportType} report: ${e.message}`);
    } finally {
      setGenerating(prev => ({ ...prev, [reportType]: false }));
    }
  };

  React.useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        await fetchMetrics();
        // Reports are generated on-demand, so we don't fetch a list
        setReports([]);
      } catch (e) {
        setError(e.response?.data?.message || 'Failed to load reports');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const reportTypes = [
    { id: 'enrollment', name: 'Enrollment Report', description: 'Student enrollment statistics', icon: Users },
    { id: 'attendance', name: 'Attendance Report', description: 'Overall attendance analysis', icon: Calendar },
    { id: 'academic', name: 'Academic Performance', description: 'Grades and performance metrics', icon: Award },
    { id: 'financial', name: 'Financial Report', description: 'Fee collection and payments', icon: TrendingUp },
    { id: 'staff', name: 'Staff Report', description: 'Staff performance and activities', icon: Users },
    { id: 'facilities', name: 'Facilities Report', description: 'Resource utilization', icon: BarChart3 }
  ];

  const systemMetrics = [
    { id: 'users', name: 'Total Users', value: metrics.totalUsers.toString(), trend: '+0%', icon: Users },
    { id: 'students', name: 'Active Students', value: metrics.activeStudents.toString(), trend: '+0%', icon: Users },
    { id: 'teachers', name: 'Teachers', value: metrics.teachers.toString(), trend: '+0%', icon: Users },
    { id: 'attendance', name: 'Avg Attendance', value: metrics.avgAttendance, trend: '+0%', icon: Calendar }
  ];

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={() => window.location.reload()}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">System Reports</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
          <Button variant="outline" size="sm">
            <Calendar className="w-4 h-4 mr-2" />
            Date Range
          </Button>
          <Button size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export All
          </Button>
        </div>
      </div>

      {/* System Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {systemMetrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <Card key={metric.id}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                    <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                    <p className="text-sm text-green-600">{metric.trend} from last month</p>
                  </div>
                  <Icon className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Report Types */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reportTypes.map((report) => {
          const Icon = report.icon;
          return (
            <Card key={report.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center text-base">
                  <Icon className="w-5 h-5 mr-2 text-blue-600" />
                  {report.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-gray-600 mb-4">{report.description}</p>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleGenerateReport(report.id)}
                    disabled={generating[report.id]}
                  >
                    {generating[report.id] ? 'Generating...' : 'Generate'}
                  </Button>
                  <Button size="sm" variant="outline" disabled>
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Reports */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Reports</CardTitle>
        </CardHeader>
        <CardContent>
          {(!reports || reports.length === 0) ? (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No reports generated yet</p>
              <p className="text-sm">Generate your first report using the options above</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reports.map((report) => (
                <div key={report.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <h4 className="font-medium">{report.name}</h4>
                    <p className="text-sm text-gray-600">{report.generatedAt}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline">
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                    <Button size="sm" variant="outline">
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
